import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Select,
  Stack,
  Alert
} from '@mui/material';
import {
  Person as PersonIcon,
  LocalHospital as MedicalIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';

import { fetchCustomerById, createCustomer, updateCustomer } from '../../redux/slices/customersSlice';
import { isCentralDispatchRole, isCentralRole, isOperatorAdminRole, isOperatorRole } from '../../redux/slices/authSlice';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';

const toDateInputValue = (value) => {
  if (!value) return '';
  const date = value.toDate
    ? value.toDate()
    : value.seconds
      ? new Date(value.seconds * 1000)
      : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const MOBILITY_MARK_OPTIONS = [
  { value: 'aG', label: 'Merkzeichen aG' },
  { value: 'Bl', label: 'Merkzeichen Bl' },
  { value: 'H', label: 'Merkzeichen H' }
];

const CARE_LEVEL_OPTIONS = [
  { value: '', label: 'Nicht hinterlegt' },
  { value: '1', label: 'Pflegegrad 1' },
  { value: '2', label: 'Pflegegrad 2' },
  { value: '3', label: 'Pflegegrad 3' },
  { value: '4', label: 'Pflegegrad 4' },
  { value: '5', label: 'Pflegegrad 5' }
];

const emptyEligibility = {
  mobilityMarks: [],
  careLevel: '',
  permanentMobilityImpairment: false,
  notes: ''
};

const normalizeEligibility = (value = {}) => ({
  mobilityMarks: Array.isArray(value.mobilityMarks) ? value.mobilityMarks : [],
  careLevel: value.careLevel ? String(value.careLevel) : '',
  permanentMobilityImpairment: value.permanentMobilityImpairment === true,
  notes: value.notes || ''
});

const CustomerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const isEditMode = !!id;
  
  const { currentCustomer, isLoading, error } = useSelector(state => state.customers);
  const currentUser = useSelector(state => state.auth.user);
  const customerManagingOperatorId = currentCustomer?.user?.managedByOperatorId ||
    currentCustomer?.user?.createdByOperatorId ||
    currentCustomer?.user?.operatorId;
  const canCreateCustomer = isCentralRole(currentUser) || isCentralDispatchRole(currentUser) || isOperatorRole(currentUser);
  const canEditCustomer = isCentralRole(currentUser) || (
    isOperatorAdminRole(currentUser) &&
    currentCustomer?.user?.isPhoneCustomer === true &&
    customerManagingOperatorId === currentUser?.operatorId
  );
  const customerTypeLabel = isEditMode
    ? currentCustomer?.user?.isPhoneCustomer === true
      ? 'Telefonkunde'
      : 'App-Kunde'
    : 'Telefonkunde';
  
  const [formData, setFormData] = useState({
    // Kunden-Informationen (nur für Telefonkunden im Admin-Panel)
    name: '',
    birthDate: '',
    phoneNumber: '',
    address: '',
    isPhoneCustomer: true, // Immer true für neue Kunden
    medicalTransportEligibility: emptyEligibility,
  });
  
  const [formErrors, setFormErrors] = useState({
    name: '',
    birthDate: '',
    phoneNumber: '',
    address: '',
  });
  
  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchCustomerById(id));
    }
  }, [dispatch, id, isEditMode]);
  
  useEffect(() => {
    if (isEditMode && currentCustomer) {
      // Beim Bearbeiten bestehender Kunden zeigen wir alle Daten an
      setFormData(prev => ({
        ...prev,
        name: currentCustomer.user?.name || '',
        birthDate: toDateInputValue(currentCustomer.user?.birthDate),
        phoneNumber: currentCustomer.user?.phoneNumber || '',
        address: currentCustomer.user?.address || '',
        medicalTransportEligibility: normalizeEligibility(currentCustomer.user?.medicalTransportEligibility),
        // isPhoneCustomer wird aus der DB übernommen, spielt beim Bearbeiten aber keine Rolle
        isPhoneCustomer: currentCustomer.user?.isPhoneCustomer || false,
      }));
    }
  }, [currentCustomer, isEditMode]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Fehler zurücksetzen, wenn der Benutzer ein Feld ändert
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const handleEligibilityChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      medicalTransportEligibility: {
        ...prev.medicalTransportEligibility,
        [field]: value
      }
    }));
  };
  
  const validateForm = () => {
    let valid = true;
    const newErrors = { ...formErrors };
    
    // Bei allen Kunden validieren wir Name und Telefonnummer
    if (!formData.name) {
      newErrors.name = 'Name ist erforderlich';
      valid = false;
    }

    if (formData.birthDate) {
      const birthDate = new Date(`${formData.birthDate}T00:00:00`);
      const minBirthDate = new Date('1900-01-01T00:00:00');
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      if (Number.isNaN(birthDate.getTime()) || birthDate < minBirthDate || birthDate > today) {
        newErrors.birthDate = 'Bitte ein plausibles Geburtsdatum eingeben';
        valid = false;
      }
    }
    
    if (!formData.phoneNumber) {
      newErrors.phoneNumber = 'Telefonnummer ist erforderlich';
      valid = false;
    }
    
    setFormErrors(newErrors);
    return valid;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();

    if ((!isEditMode && !canCreateCustomer) || (isEditMode && !canEditCustomer)) {
      return;
    }
    
    if (validateForm()) {
      if (isEditMode) {
        // Update existing customer - funktioniert für alle Kundentypen
        dispatch(updateCustomer({
          id,
          user: {
            name: formData.name,
            birthDate: formData.birthDate,
            phoneNumber: formData.phoneNumber,
            address: formData.address,
            medicalTransportEligibility: formData.medicalTransportEligibility
          }
        })).then((result) => {
          if (!result.error) {
            navigate(`/kunden/${id}`);
          }
        });
      } else {
        // Create new phone customer - Admin Web App erstellt nur Telefonkunden
        dispatch(createCustomer({
          isPhoneCustomer: true, // Immer true für Kunden, die im Admin-Panel erstellt werden
          name: formData.name,
          birthDate: formData.birthDate,
          phoneNumber: formData.phoneNumber,
          address: formData.address,
          medicalTransportEligibility: formData.medicalTransportEligibility
        })).then((result) => {
          if (!result.error) {
            navigate('/kunden');
          }
        });
      }
    }
  };
  
  const handleCancel = () => {
    if (isEditMode) {
      navigate(`/kunden/${id}`);
    } else {
      navigate('/kunden');
    }
  };
  
  if (isLoading && isEditMode) {
    return <Loading message="Daten werden geladen..." />;
  }
  
  if (error) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Daten"
        message={error}
        onRetry={() => isEditMode && dispatch(fetchCustomerById(id))}
      />
    );
  }

  if ((!isEditMode && !canCreateCustomer) || (isEditMode && currentCustomer && !canEditCustomer)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Keine Berechtigung zum {isEditMode ? 'Bearbeiten dieses Kunden' : 'Anlegen von Kunden'}.
        </Alert>
      </Box>
    );
  }
  
  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title={isEditMode ? 'Kunde bearbeiten' : 'Neuen Telefonkunden anlegen'}
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Kunden', path: '/kunden' },
          { label: isEditMode ? 'Bearbeiten' : 'Telefonkunde hinzufügen', path: isEditMode ? `/kunden/${id}/bearbeiten` : '/kunden/neu' }
        ]}
      />
      
      <Box component="form" onSubmit={handleSubmit}>
        {!isEditMode && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Telefonkunden werden ohne E-Mail und Passwort angelegt und können später in der Disposition verwendet werden.
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(360px, 0.85fr)' },
            gap: 3,
            alignItems: 'start'
          }}
        >
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                <PersonIcon color="primary" />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="h6">Kundendaten</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Basisdaten für Telefonbestellung und Disposition.
                  </Typography>
                </Box>
                <Chip
                  label={customerTypeLabel}
                  color={customerTypeLabel === 'App-Kunde' ? 'primary' : 'default'}
                  variant={customerTypeLabel === 'App-Kunde' ? 'filled' : 'outlined'}
                  size="small"
                />
              </Stack>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    id="name"
                    name="name"
                    label="Vollständiger Name"
                    value={formData.name}
                    onChange={handleChange}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    id="phoneNumber"
                    name="phoneNumber"
                    label="Telefonnummer"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    error={!!formErrors.phoneNumber}
                    helperText={formErrors.phoneNumber}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    id="birthDate"
                    name="birthDate"
                    label="Geburtsdatum"
                    type="date"
                    value={formData.birthDate}
                    onChange={handleChange}
                    error={!!formErrors.birthDate}
                    helperText={formErrors.birthDate || 'Nur für Krankenfahrten-Abrechnung relevant'}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    id="address"
                    name="address"
                    label="Anschrift"
                    value={formData.address}
                    onChange={handleChange}
                    error={!!formErrors.address}
                    helperText={formErrors.address || 'Wird bei Krankenfahrten als Startadresse vorgeschlagen.'}
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                <MedicalIcon color="primary" />
                <Box>
                  <Typography variant="h6">Krankenfahrten-Nachweis</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Stammdatenhinweis für b)-Fälle. Die Verordnung speichert später einen eigenen Snapshot.
                  </Typography>
                </Box>
              </Stack>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel id="mobilityMarks-label">Merkzeichen</InputLabel>
                    <Select
                      labelId="mobilityMarks-label"
                      multiple
                      value={formData.medicalTransportEligibility.mobilityMarks}
                      onChange={handleEligibilityChange('mobilityMarks')}
                      input={<OutlinedInput label="Merkzeichen" />}
                      renderValue={(selected) => selected.join(', ')}
                    >
                      {MOBILITY_MARK_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          <Checkbox checked={formData.medicalTransportEligibility.mobilityMarks.includes(option.value)} />
                          <ListItemText primary={option.label} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    fullWidth
                    label="Pflegegrad"
                    value={formData.medicalTransportEligibility.careLevel}
                    onChange={handleEligibilityChange('careLevel')}
                  >
                    {CARE_LEVEL_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={12}>
                  <FormControlLabel
                    sx={{ alignItems: 'flex-start', m: 0 }}
                    control={
                      <Checkbox
                        sx={{ mt: -0.5 }}
                        checked={formData.medicalTransportEligibility.permanentMobilityImpairment}
                        onChange={handleEligibilityChange('permanentMobilityImpairment')}
                      />
                    }
                    label="Bei Pflegegrad 3 liegt eine dauerhafte Mobilitätsbeeinträchtigung vor"
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Interne Notiz zum Nachweis"
                    value={formData.medicalTransportEligibility.notes}
                    onChange={handleEligibilityChange('notes')}
                    helperText="Zum Beispiel: SBA liegt vor, Pflegegradbescheid geprüft, Nachweis noch offen."
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>

        <Box
          sx={{
            mt: 3,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            flexWrap: 'wrap'
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCancel}
            startIcon={<CancelIcon />}
          >
            Abbrechen
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            disabled={isLoading}
          >
            {isEditMode ? 'Änderungen speichern' : 'Kunde erstellen'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CustomerForm;
